import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppProvider } from "./context/AppContext";
import Header from "./components/Header";
import Footer from "./components/Footer";

import Home from "./pages/Home";
import Properties from "./pages/Properties";
import PropertyDetail from "./pages/PropertyDetail";
import Locations from "./pages/Locations";
import LocationDetail from "./pages/LocationDetail";
import Compare from "./pages/Compare";
import SavedProperties from "./pages/SavedProperties";
import About from "./pages/About";
import Contact from "./pages/Contact";
import BookSiteVisit from "./pages/BookSiteVisit";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Legal from "./pages/Legal";
import DynamicPage from "./pages/DynamicPage";

import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import AdminProperties from "./pages/admin/AdminProperties";
import PropertyForm from "./pages/admin/PropertyForm";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminSiteVisits from "./pages/admin/AdminSiteVisits";
import AdminLocations from "./pages/admin/AdminLocations";
import AdminBlog from "./pages/admin/AdminBlog";
import BlogEditor from "./pages/admin/BlogEditor";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminTeam from "./pages/admin/AdminTeam";
import AdminPages from "./pages/admin/AdminPages";
import PageBuilder from "./pages/admin/PageBuilder";

function ScrollTop() {
  const { pathname } = useLocation();
  if (typeof window !== "undefined") window.scrollTo(0, 0);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Header />
      <main className="min-h-screen pt-16 md:pt-20">{children}</main>
      <Footer />
    </>
  );
}

function ProtectedAdmin({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!isAdmin) return <Navigate to="/login" replace />;
  return children;
}

const pub = (el) => <PublicLayout>{el}</PublicLayout>;

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter>
          <ScrollTop />
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={pub(<Home />)} />
            <Route path="/properties" element={pub(<Properties />)} />
            <Route path="/properties/:slug" element={pub(<PropertyDetail />)} />
            <Route path="/plots" element={pub(<Properties preset="plot" />)} />
            <Route path="/builder-floors" element={pub(<Properties preset="builder_floor" />)} />
            <Route path="/flats" element={pub(<Properties preset="flat" />)} />
            <Route path="/houses" element={pub(<Properties preset="house" />)} />
            <Route path="/commercial" element={pub(<Properties preset="commercial" />)} />
            <Route path="/urgent-sale" element={pub(<Properties special="urgent_sale" title="Urgent Sale Properties" />)} />
            <Route path="/price-reduced" element={pub(<Properties special="price_reduced" title="Price Reduced Properties" />)} />
            <Route path="/new-listings" element={pub(<Properties special="new_listing" title="New Listings" />)} />
            <Route path="/locations" element={pub(<Locations />)} />
            <Route path="/locations/:slug" element={pub(<LocationDetail />)} />
            <Route path="/compare" element={pub(<Compare />)} />
            <Route path="/saved-properties" element={pub(<SavedProperties />)} />
            <Route path="/about" element={pub(<About />)} />
            <Route path="/contact" element={pub(<Contact />)} />
            <Route path="/book-site-visit" element={pub(<BookSiteVisit />)} />
            <Route path="/blog" element={pub(<Blog />)} />
            <Route path="/blog/:slug" element={pub(<BlogDetail />)} />
            <Route path="/p/:slug" element={pub(<DynamicPage />)} />
            <Route path="/privacy-policy" element={pub(<Legal type="privacy" />)} />
            <Route path="/terms" element={pub(<Legal type="terms" />)} />
            <Route path="/disclaimer" element={pub(<Legal type="disclaimer" />)} />
            <Route path="/cookie-policy" element={pub(<Legal type="cookies" />)} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />

            <Route path="/admin" element={<ProtectedAdmin><AdminLayout /></ProtectedAdmin>}>
              <Route index element={<Dashboard />} />
              <Route path="properties" element={<AdminProperties />} />
              <Route path="properties/new" element={<PropertyForm />} />
              <Route path="properties/:id/edit" element={<PropertyForm />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="site-visits" element={<AdminSiteVisits />} />
              <Route path="locations" element={<AdminLocations />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="blog/:id" element={<BlogEditor />} />
              <Route path="pages" element={<AdminPages />} />
              <Route path="pages/:id" element={<PageBuilder />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={pub(<Home />)} />
          </Routes>
        </BrowserRouter>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
